var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { CpslButton, CpslDivider, CpslDropdown, CpslIcon, CpslInput } from '@usecapsule/react-components';
import styled from 'styled-components';
import { OAuth } from '../OAuth/OAuth.js';
import { ModalStep } from '../../utils/steps.js';
import { useCapsuleStore, useModalStore, useUserInfoStore } from '../../stores/index.js';
import { useThemeStore } from '../../stores/theme/useThemeStore.js';
import { CapsuleBlack, CapsuleWhite } from '../Icons.js';
import countryCodes from './countryCodes.js';
import parsePhoneNumberFromString from 'libphonenumber-js';
const DEFAULT_COUNTRY = { label: 'United States', value: '+1', selectedLabel: 'US', icon: 'US' };
export const SignUpStep = ({ oAuthMethods, disableEmailLogin, disablePhoneLogin }) => {
    const isDark = useThemeStore(state => state.isDark);
    const logo = useThemeStore(state => state.getLogo());
    const appName = useThemeStore(state => state.appName);
    const capsule = useCapsuleStore(state => state.capsule);
    const setFlow = useModalStore(state => state.setFlow);
    const setStep = useModalStore(state => state.setStep);
    const showAllOAuth = useModalStore(state => state.step === ModalStep.SIGN_UP_ALL_OAUTH);
    const setEmail = useUserInfoStore(state => state.setEmail);
    const email = useUserInfoStore(state => state.email);
    const setPhone = useUserInfoStore(state => state.setPhone);
    const phone = useUserInfoStore(state => state.phone);
    const setCountryCode = useUserInfoStore(state => state.setCountryCode);
    const countryCode = useUserInfoStore(state => state.countryCode);
    const setWebAuthURLForLogin = useModalStore(state => state.setWebAuthURLForLogin);
    const [emailError, setEmailError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [matchedCountryCode, setMatchedCountryCode] = useState(DEFAULT_COUNTRY);
    const handleSubmitEmail = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!email) {
            setEmailError('Email is required!');
            return;
        }
        if (!/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email)) {
            setEmailError('Please enter a valid email!');
            return;
        }
        // Logout to ensure cleared Capsule state but preserve pregen wallets
        yield capsule.logout(true);
        const userExists = yield capsule.checkIfUserExists(email);
        if (userExists) {
            const webAuthUrlForLogin = yield capsule.initiateUserLogin(email);
            setFlow('login');
            setStep(ModalStep.BIOMETRIC_LOGIN);
            setWebAuthURLForLogin(webAuthUrlForLogin);
            return;
        }
        yield capsule.createUser(email);
        setFlow('signUp');
        setStep(ModalStep.VERIFICATION_CODE);
    });
    const handleSubmitPhone = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!phone) {
            setPhoneError('Phone is required!');
            return;
        }
        // Logout to ensure cleared Capsule state but preserve pregen wallets
        yield capsule.logout(true);
        let userExists = false;
        try {
            userExists = yield capsule.checkIfUserExistsByPhone(phone, countryCode);
        }
        catch (error) {
            setPhoneError('Phone number is not valid!');
            return;
        }
        if (userExists) {
            const webAuthUrlForLogin = yield capsule.initiateUserLoginForPhone(phone, countryCode);
            setFlow('login');
            setStep(ModalStep.BIOMETRIC_LOGIN);
            setWebAuthURLForLogin(webAuthUrlForLogin);
            return;
        }
        yield capsule.createUserByPhone(phone, countryCode);
        setFlow('signUp');
        setStep(ModalStep.VERIFICATION_CODE_FOR_PHONE);
    });
    const handleEmailInput = (ev) => {
        setEmailError('');
        setEmail(ev.detail.value);
    };
    const handlePhoneInput = (ev) => {
        setPhoneError('');
        if (!checkAndSetPhoneNumberFromStringDidChange(ev.detail.value)) {
            setPhone(ev.detail.value);
        }
    };
    const handleCountryCodeInput = (ev) => {
        const matchedCountryCode = countryCodes.find(code => code.selectedLabel === ev.detail.selectedLabel);
        setCountryCode(ev.detail.value);
        setMatchedCountryCode(matchedCountryCode);
    };
    // TODO: rename this function
    const checkAndSetPhoneNumberFromStringDidChange = (inputPhone) => {
        const phoneNumber = parsePhoneNumberFromString(inputPhone);
        if (phoneNumber) {
            const countryCode = phoneNumber.country;
            const nationalNumber = phoneNumber.formatNational();
            const matchedCountryCode = countryCodes.find(code => code.selectedLabel === countryCode);
            if (matchedCountryCode) {
                setCountryCode(matchedCountryCode.value);
                setPhone(nationalNumber);
                setMatchedCountryCode(matchedCountryCode);
                return true;
            }
        }
        return false;
    };
    const handlePasteInput = (ev) => {
        var _a;
        const clipboardEvent = ev.detail;
        const paste = (_a = clipboardEvent.clipboardData) === null || _a === void 0 ? void 0 : _a.getData('text');
        if (paste) {
            checkAndSetPhoneNumberFromStringDidChange(paste);
        }
    };
    const methodsToShow = () => {
        const methods = [];
        if (!!(oAuthMethods === null || oAuthMethods === void 0 ? void 0 : oAuthMethods.length)) {
            methods.push(_jsx(OAuth, { methods: oAuthMethods }, 'oAuth'));
        }
        if (!disableEmailLogin) {
            if (methods.length > 0) {
                methods.push(_jsx(CpslDivider, { children: "or" }, 'orEmail'));
            }
            methods.push(_jsx("form", { onSubmit: e => {
                    e.preventDefault();
                    handleSubmitEmail();
                }, children: _jsxs(CpslInput, { placeholder: "Enter your email", onCpslInput: handleEmailInput, value: email, errorText: emailError, autofocus: true, inputMode: "email", onKeyDown: e => e.key === 'Enter' && handleSubmitEmail(), children: [_jsx(CpslIcon, { slot: "start", icon: "mail" }), _jsx(CpslButton, { slot: "end", onClick: handleSubmitEmail, children: _jsx(CpslIcon, { icon: "arrow" }) })] }, 'email') }));
        }
        if (!disablePhoneLogin) {
            if (methods.length > 0) {
                methods.push(_jsx(CpslDivider, { children: "or" }, 'orPhone'));
            }
            methods.push(_jsx("form", { onSubmit: e => {
                    e.preventDefault();
                    handleSubmitPhone();
                }, children: _jsxs(CpslInput, { placeholder: "Enter phone number", inputMode: "tel", autofocus: true, value: phone, errorText: phoneError, onCpslInput: handlePhoneInput, onCpslPaste: handlePasteInput, onKeyDown: e => e.key === 'Enter' && handleSubmitPhone(), children: [_jsx(CpslDropdown, { hasCpslSearch: true, selectedItem: matchedCountryCode, onSelectedItemChange: handleCountryCodeInput, slot: "start", items: countryCodes }), _jsx(CpslButton, { slot: "end", onClick: handleSubmitPhone, children: _jsx(CpslIcon, { icon: "arrow" }) })] }, 'phone') }));
        }
        return methods;
    };
    return (_jsxs(_Fragment, { children: [!showAllOAuth &&
                (logo ? (_jsx(Logo, { src: logo, alt: `${appName ? `${appName} -` : ''}logo` })) : (_jsx(LogoSvg, { children: isDark ? _jsx(CapsuleWhite, {}) : _jsx(CapsuleBlack, {}) }))), methodsToShow()] }));
};
const Logo = styled.img `
  height: 100px;
  max-width: 260px;
  object-fit: contain;
  padding: 16px 0px;
  margin: 16px 0px;
  box-sizing: content-box;
  align-self: center;
`;
const LogoSvg = styled.div `
  height: 100px;
  max-width: 260px;
  padding: 16px 0px;
  margin: 16px 0px;
  align-self: center;

  svg {
    width: 100%;
  }
`;
